import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSavedOrgs } from "@/hooks/useSavedOrgs";

/**
 * App-level store for agent → org actions whose selection must outlive a single
 * page. It owns the Compare set (per-tab working set) and the Save set (the
 * agent's persistent bookmarks, backed by Supabase via useSavedOrgs).
 *
 * Why this lives at the app root rather than in a page:
 *   1. The compare set must survive client navigation — e.g. start a comparison
 *      from an org profile, then go to Explore to pick the second org. Page-local
 *      `useState` was discarded on unmount, which is the bug this fixes.
 *   2. The CompareTray is a single app-level mount (see CompareTrayMount), so it
 *      needs one store that every surface writes to — Explore cards, the map
 *      popups, QuickView, and the profile header.
 *   3. Save likewise renders on every one of those surfaces, so hydrating the
 *      saved set once here keeps each bookmark accurate across reloads and
 *      navigation rather than letting each control hold throwaway local state.
 *
 * Structured as independent per-concern slices (`useCompareSlice`,
 * `useSavedSlice`) so each lands and evolves without disturbing the other.
 */

/** Minimal org descriptor the tray needs to render initials without re-fetching. */
export interface CompareItem {
  id: string;
  name: string;
}

interface AgentOrgActionsContextValue {
  /** Ordered org ids currently selected for comparison (max 4). */
  compareIds: string[];
  /** Selected orgs with the label the tray renders the monogram from. */
  compareItems: CompareItem[];
  /** Add/remove an org from the compare set. `name` enriches the tray monogram. */
  toggleCompare: (orgId: string, name?: string) => void;
  /** Empty the compare set. */
  clearCompare: () => void;
  /** Org ids the current user has saved (persisted, hydrated on mount). */
  savedOrgIds: string[];
  /** Save/unsave an org. `name` enriches the confirmation toast. */
  toggleSave: (orgId: string, name?: string) => void;
}

const MAX_COMPARE = 4;
const COMPARE_STORAGE_KEY = "compaire_compare_set";

const AgentOrgActionsContext = createContext<AgentOrgActionsContextValue>({
  compareIds: [],
  compareItems: [],
  toggleCompare: () => {},
  clearCompare: () => {},
  savedOrgIds: [],
  toggleSave: () => {},
});

/* ── Compare slice ──────────────────────────────────────────────────────────
 * sessionStorage, not localStorage: a comparison-in-progress is a per-tab
 * working set. It should survive a reload and client navigation, but a brand
 * new tab starts clean. In-memory state is the source of truth; sessionStorage
 * hydrates it on mount and mirrors every change.
 */

const readStoredCompare = (): CompareItem[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(COMPARE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed entries, capped at the limit.
    return parsed
      .filter((x) => x && typeof x.id === "string")
      .map((x) => ({ id: x.id as string, name: typeof x.name === "string" ? x.name : "" }))
      .slice(0, MAX_COMPARE);
  } catch {
    return [];
  }
};

const useCompareSlice = () => {
  const [compareItems, setCompareItems] = useState<CompareItem[]>(readStoredCompare);

  useEffect(() => {
    try {
      sessionStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(compareItems));
    } catch {
      /* private mode / quota — the in-memory set still works this session */
    }
  }, [compareItems]);

  const toggleCompare = useCallback((orgId: string, name = "") => {
    setCompareItems((prev) => {
      if (prev.some((o) => o.id === orgId)) return prev.filter((o) => o.id !== orgId);
      if (prev.length >= MAX_COMPARE) {
        toast({
          title: "Maximum reached",
          description: "You can compare up to 4 organizations at a time.",
          variant: "destructive",
        });
        return prev;
      }
      return [...prev, { id: orgId, name }];
    });
  }, []);

  const clearCompare = useCallback(() => setCompareItems([]), []);

  const compareIds = useMemo(() => compareItems.map((o) => o.id), [compareItems]);

  return { compareIds, compareItems, toggleCompare, clearCompare };
};

/* ── Save slice ─────────────────────────────────────────────────────────────
 * Unlike Compare, Save is durable and cross-device, so it is backed by Supabase
 * (saved_organizations) rather than storage. useSavedOrgs hydrates the current
 * user's saved set on mount and exposes an optimistic toggle; this slice just
 * binds it to the signed-in user so every Save control reads one shared set.
 */

const useSavedSlice = () => {
  const { user } = useAuth();
  return useSavedOrgs(user?.id);
};

export const AgentOrgActionsProvider = ({ children }: { children: ReactNode }) => {
  const { compareIds, compareItems, toggleCompare, clearCompare } = useCompareSlice();
  const { savedOrgIds, toggleSave } = useSavedSlice();

  const value = useMemo<AgentOrgActionsContextValue>(
    () => ({ compareIds, compareItems, toggleCompare, clearCompare, savedOrgIds, toggleSave }),
    [compareIds, compareItems, toggleCompare, clearCompare, savedOrgIds, toggleSave],
  );

  return <AgentOrgActionsContext.Provider value={value}>{children}</AgentOrgActionsContext.Provider>;
};

export const useAgentOrgActions = () => useContext(AgentOrgActionsContext);
